import React, { useState, useEffect } from "react";
import {
  Box,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fade,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { DESIGN_TOKENS } from "../design-system/tokens/colors";
import { useLocation } from "react-router-dom";
import { ChatContainerPanel, ChatProvider } from "../modules/agent";
import { ContentPanel } from "../components/content/ContentPanel";
import { ProtectedRoute } from "../components/auth";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { DashboardProvider } from "../contexts/DashboardContext";
import { AnimalPassport } from "../modules/farm/components/AnimalPassport";
import { useProfileCheck } from '../hooks/useProfileCheck';
import { useAuth } from '../hooks/useAuth';
import { CompleteProfileModal } from '../modules/profile/components/CompleteProfileModal';


const DashboardPageContent: React.FC = () => {
  const [contentView, setContentView] = useState<string>("welcome");
  const [contentData, setContentData] = useState<any>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [previousContentView, setPreviousContentView] = useState<string | null>(null);
  const [previousContentData, setPreviousContentData] = useState<any>(null);
  const [preLabContentView, setPreLabContentView] = useState<string | null>(null);
  const [preLabContentData, setPreLabContentData] = useState<any>(null);
  const [contentHistory, setContentHistory] = useState<{ view: string; data: any }[]>([]);
  const [hasSkippedProfileCompletion, setHasSkippedProfileCompletion] = useState(false);
  
  // Verificar se o usuário tem perfil completo (sem redirecionar)
  const { needsProfileCompletion } = useProfileCheck('/profile/complete', false);
  
  const showProfileModal = needsProfileCompletion && !hasSkippedProfileCompletion;
  
  // Obter dados do usuário para debug
  const { authState } = useAuth();
  
  // Se precisa completar perfil, o hook já redireciona automaticamente
  // Removido o retorno null para permitir renderizar o modal em cima do dashboard borrado
  /*
  if (needsProfileCompletion) {
    return null; // Componente será desmontado durante o redirecionamento
  }
  */

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  // Views that should automatically open the drawer on mobile
  // Other views (contextual content from chat) will stay hidden on mobile unless manually opened
  const MOBILE_DRAWER_VIEWS = [
    'profile',
    'settings',
    'userManagement',
    'billing',
    'agenda',
    'professional_dashboard'
  ];

  // Helper para identificar telas de laboratório
  const isLabView = (view?: string) => {
    if (!view) return false;
    return [
      'examsHub',
      'mastitisIntro',
      'mastitisTeatStep',
      'mastitisAnimalIdStep',
      'mastitisTestStep1',
      'mastitisTestStep2',
      'mastitisTestStep3',
      'exam-list',
      'exam-detail',
    ].includes(view);
  };

  // Lê estado de navegação (opcional)
  useEffect(() => {
    const state = location.state as {
      contentView?: string;
      contentData?: any;
    } | null;

    if (state?.contentView) {
      setContentView(state.contentView);
      if (state.contentData) setContentData(state.contentData);
      
      // Also trigger drawer logic for navigation updates
      if (isMobile && MOBILE_DRAWER_VIEWS.includes(state.contentView)) {
        setMobileDrawerOpen(true);
      }
      
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleIntentUpdate = (view: string, data?: any) => {
    // Guardar anterior sempre
    setContentHistory(prev => [...prev, { view: contentView, data: contentData }]);
    setPreviousContentView(contentView);
    setPreviousContentData(contentData);

    // Se estamos entrando no domínio de laboratório vindo de um conteúdo não-laboratorial,
    // guardar o pré-laboratório para restauração futura
    if (isLabView(view) && !isLabView(contentView)) {
      setPreLabContentView(contentView);
      setPreLabContentData(contentData);
    }

    // Se estamos saindo do domínio de laboratório para um conteúdo normal, resetar o pré-lab
    if (!isLabView(view) && isLabView(contentView)) {
      setPreLabContentView(null);
      setPreLabContentData(null);
    }

    setContentView(view);
    setContentData(data);
    
    // On mobile, only open drawer automatically for specific views
    if (isMobile) {
      if (MOBILE_DRAWER_VIEWS.includes(view)) {
        setMobileDrawerOpen(true);
      } else {
        // Keep closed for chat context updates to avoid interruption
        setMobileDrawerOpen(false);
      }
    }
  };

  const restorePreviousContent = () => {
    if (preLabContentView) {
      setContentView(preLabContentView);
      setContentData(preLabContentData);
      setPreLabContentView(null);
      setPreLabContentData(null);
      return;
    }
    if (contentHistory.length > 0) {
      const last = contentHistory[contentHistory.length - 1];
      const newHist = contentHistory.slice(0, -1);
      setContentView(last.view);
      setContentData(last.data);
      const prev = newHist[newHist.length - 1] || null;
      setPreviousContentView(prev?.view || null);
      setPreviousContentData(prev?.data || null);
      setContentHistory(newHist);
      return;
    }
    if (previousContentView) {
      setContentView(previousContentView);
      setContentData(previousContentData);
      return;
    }
    setContentView('welcome');
    setContentData(null);
  };

  const getPreviousView = () => {
    if (preLabContentView) return preLabContentView;
    if (contentHistory.length > 0) {
      return contentHistory[contentHistory.length - 1].view;
    }
    if (previousContentView) return previousContentView;
    return 'welcome';
  };

  const handleMobileDrawerBack = () => {
    const nextView = getPreviousView();
    restorePreviousContent();
    if (isMobile) {
      if (MOBILE_DRAWER_VIEWS.includes(nextView)) {
        setMobileDrawerOpen(true);
      } else {
        setMobileDrawerOpen(false);
      }
    }
  };

  const ChatPanelComponent = <ChatContainerPanel />;

  return (
    <DashboardProvider 
      updateContent={handleIntentUpdate}
      contentView={contentView}
      contentData={contentData}
      previousContentView={previousContentView}
      previousContentData={previousContentData}
      restorePreviousContent={restorePreviousContent}
    >
      <ChatProvider>
        {/* Modal de Completar Perfil */}
        <CompleteProfileModal 
          open={showProfileModal} 
          onClose={() => setHasSkippedProfileCompletion(true)}
        />

        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            bgcolor: DESIGN_TOKENS.colors.background.default,
            filter: showProfileModal ? 'blur(8px)' : 'none',
            pointerEvents: showProfileModal ? 'none' : 'auto',
            transition: 'filter 0.3s ease',
          }}
        >
          <Box
            sx={{
              flex: 1,
              backgroundColor: DESIGN_TOKENS.colors.background.elevated,
              overflow: "hidden",
              position: "relative",
              minHeight: 0,
            }}
          >
            {isMobile ? (
              // ====== MOBILE ======
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  minHeight: 0,
                  backgroundColor: DESIGN_TOKENS.colors.primary[700],
                }}
              >
                {/* Chat Panel ocupando toda a tela em mobile */}
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0,
                  }}
                >
                  {ChatPanelComponent}
                </Box>

                {/* Mobile Content Drawer */}
                <Drawer
                  anchor="right"
                  open={mobileDrawerOpen}
                  onClose={() => setMobileDrawerOpen(false)}
                  PaperProps={{
                    sx: { width: '100%', maxWidth: '100%' }
                  }}
                  ModalProps={{
                    keepMounted: true // Better performance for frequent opening
                  }}
                >
                  <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <ContentPanel
                      contentView={contentView}
                      contentData={contentData}
                      onIntentUpdate={handleIntentUpdate}
                      onClose={handleMobileDrawerBack}
                    />
                  </Box>
                </Drawer>
              </Box>
            ) : (
              // ====== DESKTOP ======
              <Box
                sx={{
                  display: "flex",
                  height: "100%",
                  overflow: "hidden",
                  minHeight: 0,
                }}
              >
                {/* Painel do Chat à esquerda */}
                <Box
                  sx={{
                    width: { md: 350, lg: 400 },
                    flexShrink: 0,
                    borderRight: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                    backgroundColor: DESIGN_TOKENS.colors.primary[700],
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    height: "100%",
                  }}
                >
                  {ChatPanelComponent}
                </Box>

                {/* Painel de conteúdo à direita */}
                {contentView === "animal-passport" &&
                contentData?.animalData ? (
                  <AnimalPassport animalData={contentData.animalData} />
                ) : (
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                      minHeight: 0,
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{ flex: 1, p: 3, overflow: "hidden", minHeight: 0 }}
                    >
                      <Fade in timeout={300}>
                          <Box sx={{ height: "100%", minHeight: 0 }}>
                            <ContentPanel
                            contentView={contentView}
                            contentData={contentData}
                            onIntentUpdate={handleIntentUpdate}
                          />
                        </Box>
                      </Fade>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </ChatProvider>
    </DashboardProvider>
  );
};

export const DashboardPage: React.FC = () => {
  return (
    <ProtectedRoute requiredRole="USER" requireActivePlan paymentRedirectPath="/subscription-required">
      <DashboardLayout>
        <DashboardPageContent />
      </DashboardLayout>
    </ProtectedRoute>
  );
};
